import React, { useEffect, useState } from 'react';
import { useAdViewability } from '../hooks/useAdViewability';

/**
 * Enhanced AdSense Unit with:
 * - Responsive container sizing
 * - Viewability monitoring
 * - A/B test variant support
 * - Performance tracking
 */
const EnhancedAdSenseUnit = ({ 
  slot = 'auto', 
  format = 'auto', 
  responsive = true, 
  position = 'default',
  variant = 'control',
  className = ''
}) => {
  const [adLoaded, setAdLoaded] = useState(false);
  const { ref, isViewable, viewableTime, impressionTracked } = useAdViewability(variant, position);
  
  useEffect(() => {
    try {
      if (window.adsbygoogle && process.env.NODE_ENV !== 'development') {
        (window.adsbygoogle = window.adsbygoogle || [])?.push({});
        setAdLoaded(true);
      }
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, []);

  const adsenseId = import.meta.env?.VITE_ADSENSE_ID;

  if (!adsenseId || adsenseId === 'your-adsense-id-here') {
    return null;
  }

  // Responsive container sizing based on format and screen size
  const getContainerClasses = () => {
    const baseClasses = 'w-full flex justify-center items-center';
    
    switch (format) {
      case 'horizontal':
        return `${baseClasses} min-h-[90px] md:min-h-[120px]`;
      case 'vertical':
        return `${baseClasses} min-h-[250px] md:min-h-[600px]`;
      case 'rectangle':
        return `${baseClasses} min-h-[250px] md:min-h-[280px]`;
      default:
        return `${baseClasses} min-h-[100px] md:min-h-[150px]`;
    }
  };

  // Get responsive ad styles
  const getAdStyles = () => {
    const baseStyles = {
      display: 'block',
      textAlign: 'center'
    };

    // Format-specific sizing
    switch (format) {
      case 'horizontal':
        return {
          ...baseStyles,
          width: '100%',
          maxWidth: '970px',
          minHeight: '90px'
        };
      case 'vertical':
        return {
          ...baseStyles,
          width: '100%',
          maxWidth: '300px',
          minHeight: '250px'
        };
      case 'rectangle':
        return {
          ...baseStyles,
          width: '100%',
          maxWidth: '336px',
          minHeight: '280px'
        };
      default:
        return {
          ...baseStyles,
          width: '100%',
          minHeight: '100px'
        };
    }
  };

  return (
    <div 
      ref={ref}
      className={`${getContainerClasses()} my-4 md:my-6 lg:my-8 ${className}`}
      data-ad-position={position}
      data-ad-variant={variant}
      data-viewable={isViewable}
      data-impression-tracked={impressionTracked}
    >
      <ins
        className="adsbygoogle"
        style={getAdStyles()}
        data-ad-client={adsenseId}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive?.toString()}
      ></ins>
      {/* Development mode indicator */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
          <div>Variant: {variant}</div>
          <div>Position: {position}</div>
          <div>Viewable: {isViewable ? '✓' : '✗'}</div>
          <div>Time: {(viewableTime / 1000)?.toFixed(1)}s</div>
        </div>
      )}
    </div>
  );
};

export default EnhancedAdSenseUnit;